/**
 * SMC Liquidity Analysis Card - تحليل سيولة المؤسسات
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Crosshair, TrendingUp, TrendingDown, Minus, Droplets, Layers, Box, RefreshCw } from 'lucide-react';
import { fetchSmcAnalysis } from '../../lib/trading-api';

const SmcAnalysisCard = ({ language = 'ar', symbol = 'XAUUSD' }) => {
  const ar = language === 'ar';
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSmcAnalysis(symbol);
      setAnalysis(data);
    } catch (err) {
      console.error('SMC analysis failed:', err);
    }
    setLoading(false);
  }, [symbol]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  if (!analysis) {
    return (
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-6 h-6 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { structure, session, sweep, signal, liquidity_pools, order_blocks, fair_value_gaps } = analysis;

  const trendIcon = structure.trend === 'bullish'
    ? <TrendingUp className="h-4 w-4 text-emerald-400" />
    : structure.trend === 'bearish'
      ? <TrendingDown className="h-4 w-4 text-red-400" />
      : <Minus className="h-4 w-4 text-amber-400" />;

  const trendLabel = {
    bullish: ar ? 'صاعد' : 'Bullish',
    bearish: ar ? 'هابط' : 'Bearish',
    ranging: ar ? 'عرضي' : 'Ranging',
  }[structure.trend];

  const sessionLabel = {
    asia: ar ? 'آسيا (سيولة منخفضة)' : 'Asia (low liquidity)',
    london: ar ? 'لندن (سيولة عالية)' : 'London (high liquidity)',
    london_ny_overlap: ar ? 'تقاطع لندن/نيويورك (ذروة السيولة)' : 'London/NY overlap (peak liquidity)',
    new_york: ar ? 'نيويورك (سيولة عالية)' : 'New York (high liquidity)',
    off_hours: ar ? 'خارج الجلسات' : 'Off hours',
  }[session.session] || session.session;

  const actionColor = signal.action === 'buy' ? 'text-emerald-400 bg-emerald-500/20'
    : signal.action === 'sell' ? 'text-red-400 bg-red-500/20'
      : 'text-amber-400 bg-amber-500/20';

  const actionLabel = signal.action === 'buy' ? (ar ? 'شراء' : 'BUY')
    : signal.action === 'sell' ? (ar ? 'بيع' : 'SELL')
      : (ar ? 'انتظار' : 'HOLD');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-5"
      data-testid="smc-analysis-card"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <Crosshair className="h-5 w-5 text-[#d4af37]" />
          {ar ? 'تحليل سيولة المؤسسات (SMC)' : 'Institutional Liquidity (SMC)'}
          <span className="text-xs text-[#71717A]">{analysis.symbol} · {analysis.timeframe}</span>
        </h3>
        <button onClick={load} disabled={loading} className="text-[#71717A] hover:text-[#d4af37] transition-colors" data-testid="smc-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Signal banner */}
      <div className={`rounded-lg p-3 mb-4 ${actionColor.split(' ')[1]} border border-white/5`} data-testid="smc-signal-banner">
        <div className="flex items-center justify-between">
          <span className={`font-bold text-lg ${actionColor.split(' ')[0]}`}>{actionLabel}</span>
          <span className="text-white text-sm">{ar ? 'الثقة' : 'Confidence'}: {Math.round((signal.confidence || 0) * 100)}%</span>
        </div>
        <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">{signal.reason}</p>
        {signal.sl_hint && (
          <div className="flex gap-4 mt-2 text-xs">
            <span className="text-red-400">SL: {signal.sl_hint}</span>
            {signal.tp_hint && <span className="text-emerald-400">TP: {signal.tp_hint}</span>}
          </div>
        )}
      </div>

      {/* Context grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[#71717A] text-xs mb-1">{ar ? 'هيكل السوق' : 'Structure'}</div>
          <div className="flex items-center gap-1.5 text-white">{trendIcon} {trendLabel}</div>
          {structure.event && <div className="text-[10px] text-[#d4af37] mt-0.5">{structure.event}</div>}
        </div>
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[#71717A] text-xs mb-1">{ar ? 'الجلسة' : 'Session'}</div>
          <div className="text-white text-xs">{sessionLabel}</div>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[#71717A] text-xs mb-1 flex items-center gap-1">
            <Droplets className="h-3 w-3" /> {ar ? 'اصطياد السيولة' : 'Liquidity Sweep'}
          </div>
          <div className={sweep ? 'text-emerald-400 text-xs font-semibold' : 'text-[#71717A] text-xs'}>
            {sweep
              ? (ar ? `مكتشف ${sweep.direction === 'bullish' ? '↑ صاعد' : '↓ هابط'} عند ${sweep.pool?.price}` : `Detected ${sweep.direction} @ ${sweep.pool?.price}`)
              : (ar ? 'لا يوجد حالياً' : 'None detected')}
          </div>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[#71717A] text-xs mb-1 flex items-center gap-1">
            <Layers className="h-3 w-3" /> {ar ? 'مناطق المؤسسات' : 'Institutional Zones'}
          </div>
          <div className="text-white text-xs">
            {liquidity_pools.length} {ar ? 'تجمع سيولة' : 'pools'} · {order_blocks.length} OB · {fair_value_gaps.length} FVG
          </div>
        </div>
      </div>

      {/* Top liquidity pools */}
      {liquidity_pools.length > 0 && (
        <div className="mt-4">
          <div className="text-[#71717A] text-xs mb-2 flex items-center gap-1">
            <Box className="h-3 w-3" /> {ar ? 'أقوى تجمعات السيولة' : 'Strongest Liquidity Pools'}
          </div>
          <div className="flex flex-wrap gap-2">
            {liquidity_pools.slice(0, 5).map((pool, idx) => (
              <span
                key={`${pool.price}-${pool.side}`}
                className={`text-xs px-2 py-1 rounded-full border ${
                  pool.side === 'buy_side'
                    ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10'
                    : 'border-red-500/30 text-red-400 bg-red-500/10'
                }`}
                data-testid={`liquidity-pool-${idx}`}
              >
                {pool.price} {pool.side === 'buy_side' ? '▲' : '▼'} ×{pool.touches}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SmcAnalysisCard;
